import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Alert, Card, Spinner, StatusPill } from '../../components/ui';
import { api, ApiError } from '../../lib/api';
import { brl } from '../../lib/format';
import { usePageMeta } from '../../lib/seo';
import { tr } from '../../lib/i18n';

/* ------------------------------------------------------------------ */
/* Utilidades compartilhadas pelas telas administrativas               */
/* A navegação pertence ao AdminLayout; as telas só exibem o conteúdo. */
/* ------------------------------------------------------------------ */

export function AdminScreen({
  title, description, children
}: { title: string; description: string; children: ReactNode }): JSX.Element {
  return (
    <div className="pb-8">
      <header className="mb-4">
        <h1 className="text-3xl">{title}</h1>
        <p className="nl-hint mt-1 max-w-3xl">{description}</p>
      </header>
      {children}
    </div>
  );
}

export function adminErrorText(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 401) return 'Sua sessão expirou. Entre novamente pela entrada da administração para continuar.';
    if (error.status === 403) return error.message || 'Você não tem permissão de administrador para esta área.';
    if (error.status === 404) return 'Registro não encontrado nesta base.';
    return error.message || 'Não foi possível concluir a operação.';
  }
  return 'Falha de conexão com o servidor. Verifique sua internet e tente novamente.';
}

export interface Resource<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => Promise<void>;
}

export function useResource<T>(loader: () => Promise<T>, deps: readonly unknown[] = []): Resource<T> {
  const ref = useRef(loader);
  ref.current = loader;
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await ref.current());
    } catch (e) {
      setError(adminErrorText(e));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { data, error, loading, reload };
}

export function subscriptionTone(status: string | null | undefined): 'neutral' | 'positive' | 'warning' | 'danger' {
  if (!status) return 'neutral';
  if (status === 'active') return 'positive';
  if (status === 'trialing' || status === 'past_due') return 'warning';
  if (status === 'suspended' || status === 'canceled' || status === 'expired') return 'danger';
  return 'neutral';
}

export function LoadOrError({ loading, error }: { loading: boolean; error: string | null }): JSX.Element | null {
  if (loading) return <div className="py-10 text-center"><Spinner label={tr("Carregando dados da plataforma")} /></div>;
  if (error) return <div className="mb-4"><Alert tone="danger" title={tr("Não foi possível carregar")}>{error}</Alert></div>;
  return null;
}

/* ------------------------------------------------------------------ */
/* Visão geral                                                          */
/* ------------------------------------------------------------------ */

interface OverviewStats {
  users: number;
  businesses: number;
  trialing: number;
  active: number;
  pastDue: number;
  appointmentsTotal: number;
  revenueMonthCents: number;
  revenueTotalCents: number;
}

interface OverviewPayload {
  stats: OverviewStats;
  perPlan: { name: string; subscriptions: number }[];
}

interface PublicPlansPayload {
  trialDays: number;
  paymentConfigured: boolean;
  pixEnabled: boolean;
  plans: unknown[];
}

interface HealthPayload {
  ok: boolean;
  app: string;
  paymentProvider: string;
}

interface SettingsPayload {
  settings: Record<string, string>;
}

function Metric({ label, value, hint }: { label: string; value: string; hint?: string }): JSX.Element {
  return (
    <Card className="h-full">
      <p className="nl-label mb-0 text-muted">{label}</p>
      <p className="mt-2 font-display text-3xl font-semibold text-graphite">{value}</p>
      {hint ? <p className="nl-hint mt-1">{hint}</p> : null}
    </Card>
  );
}

export function AdminOverviewPage(): JSX.Element {
  usePageMeta('Painel administrativo', 'Visão geral da plataforma AgendaPro.', '/admin');

  const overview = useResource<OverviewPayload>(() => api.get<OverviewPayload>('/api/admin/overview'));
  const plansInfo = useResource<PublicPlansPayload>(() => api.get<PublicPlansPayload>('/api/plans'));
  const health = useResource<HealthPayload>(() => api.get<HealthPayload>('/api/health'));
  const settings = useResource<SettingsPayload>(() => api.get<SettingsPayload>('/api/admin/settings'));

  const stats = overview.data?.stats ?? null;
  const provider = health.data?.paymentProvider ?? 'desconhecido';
  const paymentConfigured = plansInfo.data?.paymentConfigured ?? false;
  const pixEnabled = plansInfo.data?.pixEnabled ?? false;
  const pixKey = settings.data?.settings.pix_key?.trim() ?? '';
  const pixReceiver = settings.data?.settings.pix_merchant_name?.trim() ?? '';
  const supportWhatsapp = settings.data?.settings.support_whatsapp?.trim() ?? '';
  const supportEmail = settings.data?.settings.support_email?.trim() ?? '';

  return (
    <AdminScreen
      title={tr("Visão geral da plataforma")}
      description={tr("Números reais lidos do backend: usuários, estabelecimentos ativos, assinaturas por situação, agendamentos e receita confirmada em pagamentos.")}
    >
      <LoadOrError loading={overview.loading} error={overview.error} />

      {stats ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Metric label={tr("Usuários ativos")} value={String(stats.users)} hint="Contas vinculadas a estabelecimentos ativos." />
          <Metric label={tr("Estabelecimentos ativos")} value={String(stats.businesses)} hint="Somente status ativo." />
          <Metric label={tr("Agendamentos totais")} value={String(stats.appointmentsTotal)} hint="Todos os períodos." />
          <Metric label={tr("Receita confirmada (mês)")} value={brl(stats.revenueMonthCents)} hint={tr("Transações pagas desde o dia 1.")} />
        </div>
      ) : null}

      {stats ? (
        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <Card>
            <h2 className="text-xl">{tr("Assinaturas por situação")}</h2>
            <p className="nl-hint mt-1">{tr("A API expõe apenas estas três situações agregadas.")}</p>
            <ul className="mt-4 space-y-3">
              <li className="flex items-center justify-between gap-3">
                <StatusPill tone="positive">{tr("Assinatura ativa")}</StatusPill>
                <span className="font-semibold tabular-nums">{stats.active}</span>
              </li>
              <li className="flex items-center justify-between gap-3">
                <StatusPill tone="warning">{tr("Em teste gratuito")}</StatusPill>
                <span className="font-semibold tabular-nums">{stats.trialing}</span>
              </li>
              <li className="flex items-center justify-between gap-3">
                <StatusPill tone="warning">{tr("Pagamento pendente")}</StatusPill>
                <span className="font-semibold tabular-nums">{stats.pastDue}</span>
              </li>
              <li className="flex items-center justify-between gap-3 border-t border-line pt-3">
                <span className="text-sm font-semibold">{tr("Receita confirmada acumulada")}</span>
                <span className="font-semibold tabular-nums">{brl(stats.revenueTotalCents)}</span>
              </li>
            </ul>
          </Card>

          <Card>
            <h2 className="text-xl">{tr("Assinaturas por plano")}</h2>
            <p className="nl-hint mt-1">{tr("Contagem de assinaturas em teste, ativa ou com pagamento pendente.")}</p>
            {overview.loading ? <div className="mt-4"><Spinner /></div> : null}
            {overview.data?.perPlan.length ? (
              <ul className="mt-4 space-y-2">
                {overview.data.perPlan.map((row) => (
                  <li key={row.name} className="flex items-center justify-between gap-3 rounded-md bg-surface px-3 py-2">
                    <span className="font-medium">{row.name}</span>
                    <span className="text-sm text-muted tabular-nums">
                      {row.subscriptions} {row.subscriptions === 1 ? 'assinatura' : 'assinaturas'}
                    </span>
                  </li>
                ))}
              </ul>
            ) : null}
            {overview.error ? <div className="mt-4"><Alert tone="danger">{overview.error}</Alert></div> : null}
          </Card>
        </div>
      ) : null}

      <Card className="mt-4">
        <h2 className="text-xl">{tr("Estado das integrações")}</h2>
        <p className="nl-hint mt-1">
          {tr("Indicadores reportados pelo próprio backend. O que a API não expõe está marcado como não reportado — nada é inventado aqui.")}
        </p>
        <ul className="mt-4 space-y-3">
          <li className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-line px-3 py-2">
            <span>
              <span className="font-semibold">{tr("Forma de pagamento")}</span>
              <span className="nl-hint block">
                {paymentConfigured
                  ? tr('Cobrança automática pelo provedor {provider}.', { provider })
                  : pixEnabled
                    ? pixReceiver
                      ? tr('Pix na chave {key} (recebedor {receiver}) — QR com o valor do plano em Meu plano e em Ativações. Sem gateway: a licença é ativada na confirmação manual do comprovante.', { key: pixKey, receiver: pixReceiver })
                      : tr('Pix na chave {key} — QR com o valor do plano em Meu plano e em Ativações. Sem gateway: a licença é ativada na confirmação manual do comprovante.', { key: pixKey })
                    : tr('Nenhuma forma de pagamento configurada. Preencha a chave Pix em Configurações.')}
              </span>
            </span>
            {plansInfo.loading ? (
              <Spinner label={tr("Verificando")} />
            ) : paymentConfigured ? (
              <StatusPill tone="positive">{tr("Provedor automático")}</StatusPill>
            ) : pixEnabled ? (
              <StatusPill tone="positive">{tr("Pix ativo — confirmação manual")}</StatusPill>
            ) : (
              <StatusPill tone="danger">{tr("Não configurado")}</StatusPill>
            )}
          </li>
          <li className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-line px-3 py-2">
            <span>
              <span className="font-semibold">{tr("E-mail transacional (SMTP)")}</span>
              <span className="nl-hint block">
                {tr("Nenhuma rota GET devolve o estado do SMTP; somente")} <code>{tr("POST /api/auth/forgot-password")}</code> {tr("informa")} <code>{tr("emailConfigured")}</code>.
              </span>
            </span>
            <StatusPill tone="neutral">{tr("Não reportado pela API")}</StatusPill>
          </li>
          <li className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-line px-3 py-2">
            <span>
              <span className="font-semibold">{tr("WhatsApp de suporte")}</span>
              <span className="nl-hint block">{tr("Valor de")} <code>{tr("support_whatsapp")}</code> {tr("nas configurações do site.")}</span>
            </span>
            {settings.loading ? (
              <Spinner label={tr("Verificando")} />
            ) : supportWhatsapp ? (
              <StatusPill tone="positive">{tr("Preenchido")}</StatusPill>
            ) : (
              <StatusPill tone="warning">{tr("Vazio — configure em Configurações")}</StatusPill>
            )}
          </li>
          <li className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-line px-3 py-2">
            <span>
              <span className="font-semibold">{tr("E-mail de suporte")}</span>
              <span className="nl-hint block">{tr("Valor de")} <code>{tr("support_email")}</code> {tr("nas configurações do site.")}</span>
            </span>
            {settings.loading ? (
              <Spinner label={tr("Verificando")} />
            ) : supportEmail ? (
              <StatusPill tone="positive">{supportEmail}</StatusPill>
            ) : (
              <StatusPill tone="warning">{tr("Vazio — configure em Configurações")}</StatusPill>
            )}
          </li>
          <li className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-line px-3 py-2">
            <span>
              <span className="font-semibold">{tr("Teste gratuito")}</span>
              <span className="nl-hint block">{tr("Dias de teste devolvidos por")} <code>{tr("GET /api/plans")}</code>.</span>
            </span>
            <StatusPill tone="neutral">
              {plansInfo.data ? `${plansInfo.data.trialDays} dias` : '—'}
            </StatusPill>
          </li>
        </ul>
        {overview.error ? <div className="mt-4"><Alert tone="danger">{overview.error}</Alert></div> : null}
      </Card>
    </AdminScreen>
  );
}
